import apiService from './apiService';

export const skillsService = {
  // ==================== Skills Directory ====================

  async getSkills(params?: { category?: string; search?: string }): Promise<any> {
    return apiService.get('/skills', { params });
  },

  async getSkillDirectory(params?: { category?: string; search?: string }): Promise<any> {
    return apiService.get('/skills/directory', { params });
  },

  async getMySkills(): Promise<any> {
    return apiService.get('/skills/me');
  },

  // ==================== Mentors ====================

  async getMentors(params?: { skillId?: string; availability?: boolean }): Promise<any> {
    return apiService.get('/skills/mentors', { params });
  },

  async registerMentorSkill(data: {
    skillName: string;
    expertiseLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    willingToMentor?: boolean;
    seekingMentorship?: boolean;
    visibility?: 'private' | 'manager' | 'team';
  }): Promise<any> {
    return apiService.post('/skills/mentors', data);
  },

  async updateMentorSkill(
    skillId: string,
    data: {
      expertiseLevel?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
      willingToMentor?: boolean;
      seekingMentorship?: boolean;
      visibility?: 'private' | 'manager' | 'team';
    }
  ): Promise<any> {
    return apiService.put(`/skills/mentors/${skillId}`, data);
  },

  async unregisterMentorSkill(skillId: string): Promise<void> {
    return apiService.delete(`/skills/mentors/${skillId}`);
  },

  // ==================== Matching ====================

  async findMatches(params?: { skillName?: string; limit?: number }): Promise<any> {
    return apiService.get('/skills/match', { params });
  },

  // ==================== Help Requests ====================

  async getHelpRequests(params?: { status?: string; skillId?: string; page?: number; limit?: number }): Promise<any> {
    return apiService.get('/skills/help-requests', { params });
  },

  async getHelpRequest(id: string): Promise<any> {
    return apiService.get(`/skills/help-requests/${id}`);
  },

  async createHelpRequest(data: {
    title: string;
    description: string;
    skillsNeeded: string[];
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    teamId?: string;
    sourceType?: 'manual' | 'check_in' | 'ai_suggestion';
    expiresInDays?: number;
  }): Promise<any> {
    return apiService.post('/skills/help-requests', data);
  },

  async updateHelpRequest(
    id: string,
    data: {
      title?: string;
      description?: string;
      skillsNeeded?: string[];
      priority?: 'low' | 'normal' | 'high' | 'urgent';
      status?: 'open' | 'in_progress' | 'resolved' | 'expired' | 'cancelled';
    }
  ): Promise<any> {
    return apiService.put(`/skills/help-requests/${id}`, data);
  },

  async deleteHelpRequest(id: string): Promise<void> {
    return apiService.delete(`/skills/help-requests/${id}`);
  },

  async respondToHelpRequest(id: string, data: { message: string }): Promise<any> {
    return apiService.post(`/skills/help-requests/${id}/respond`, data);
  },

  // ==================== Connections ====================

  async getConnections(params?: { status?: string; page?: number; limit?: number }): Promise<any> {
    return apiService.get('/skills/connections', { params });
  },

  async getConnectionStats(): Promise<any> {
    return apiService.get('/skills/connections/stats');
  },

  async createConnection(data: { mentorId: string; skillId: string; message?: string }): Promise<any> {
    return apiService.post('/skills/connections', data);
  },

  async updateConnection(id: string, data: { status?: string; rating?: number; feedback?: string }): Promise<any> {
    return apiService.put(`/skills/connections/${id}`, data);
  },

  // ==================== Availability ====================

  async getAvailability(): Promise<any> {
    return apiService.get('/skills/availability');
  },

  async updateAvailability(data: {
    available: boolean;
    hoursPerWeek?: number;
    preferredDays?: string[];
    preferredTimes?: string[];
    preferredMethod?: string;
  }): Promise<any> {
    // Map frontend fields to backend expected fields
    const backendData: any = {
      isAvailable: data.available,
    };
    if (data.hoursPerWeek !== undefined) {
      backendData.maxRequestsPerWeek = data.hoursPerWeek;
    }
    if (data.preferredTimes && data.preferredTimes.length >= 2) {
      backendData.availabilityHours = JSON.stringify({
        start: data.preferredTimes[0],
        end: data.preferredTimes[1],
      });
    }
    if (data.preferredMethod) {
      backendData.preferredConnectionMethod = data.preferredMethod;
    }
    return apiService.put('/skills/availability', backendData);
  },
};
